import { getUserProfile } from '@/api/user';
import { getAccessToken, getWalletAddress, setWalletAddress } from '@/utils/auth-token';
import { getPrivyEmbeddedSignerAddress } from '@/utils/privy';
import { sessionExpectsCustodialWallet } from '@/utils/session-login-track';
import {
  addressesEqual,
  fetchTradeAddressFromList,
  getTradeAddressType,
  pickCustodialTradeAddressRecord,
} from '@/utils/trade-address';

function pickWalletFromProfile(profile) {
  if (!profile || typeof profile !== 'object') return '';
  const candidates = [
    profile.walletAddress,
    profile.wallet,
    profile.address,
    profile.ethAddress,
    profile.userAddress,
  ];
  for (const value of candidates) {
    const trimmed = String(value ?? '').trim();
    if (trimmed) return trimmed;
  }
  return '';
}

async function resolvePrivyTradeUserAddress() {
  let embeddedAddress = '';
  try {
    embeddedAddress = String(await getPrivyEmbeddedSignerAddress()).trim();
  } catch (err) {
    console.warn('[wallet] Privy signer unavailable', err);
  }

  if (embeddedAddress) {
    const { address: fromList, records } = await fetchTradeAddressFromList({
      embeddedAddress,
    });
    const matchingRegistered = Array.isArray(records)
      ? records.find(
          (item) =>
            getTradeAddressType(item) === 2 && addressesEqual(item?.address, embeddedAddress)
        )
      : null;
    const registered = matchingRegistered || pickCustodialTradeAddressRecord(records);
    // 后台已有托管地址时以资金身份记录为准。若 signer 不同，后续 assert 会
    // 触发一次安全恢复并最终报 identity conflict，绝不能把缓存改成新钱包。
    const resolved = String(registered?.address ?? '').trim() || fromList || embeddedAddress;
    setWalletAddress(resolved);
    return resolved;
  }

  const { address: fromList, records } = await fetchTradeAddressFromList();
  if (fromList) {
    setWalletAddress(fromList);
    return fromList;
  }

  const cached = getWalletAddress();
  if (cached) {
    const isCustodialInList =
      Array.isArray(records) &&
      records.some(
        (item) => addressesEqual(item?.address, cached) && getTradeAddressType(item) === 2
      );
    if (isCustodialInList || !records?.length) {
      return cached;
    }
  }

  return '';
}

/**
 * 解析下单所需钱包地址：prop → Privy signer / trade-address/list (托管) / localStorage / profile (自管)
 * @param {string} [preferred]
 */
export async function resolveTradeUserAddress(preferred = '') {
  const fromPreferred = String(preferred ?? '').trim();

  // 仅按本次 Session 轨分流；禁止用残留 provider=privy 覆盖 WALLET 登录
  if (sessionExpectsCustodialWallet()) {
    let embeddedAddress = '';
    try {
      embeddedAddress = String(await getPrivyEmbeddedSignerAddress()).trim();
    } catch (err) {
      // ignore
    }

    if (fromPreferred && (!embeddedAddress || addressesEqual(fromPreferred, embeddedAddress))) {
      return fromPreferred;
    }

    return resolvePrivyTradeUserAddress();
  }

  // Dual-liquidity：WALLET 登录但 users 已换绑 type=2 → 下单 maker 必须是 exec Privy。
  // 不写 setWalletAddress，保留本地 SIWE/连接 EOA 缓存，避免顶栏显示被改成 Privy。
  try {
    const { records } = await fetchTradeAddressFromList();
    const custodial = pickCustodialTradeAddressRecord(records);
    const execAddr = String(custodial?.address ?? '').trim();
    if (execAddr) {
      if (fromPreferred && addressesEqual(fromPreferred, execAddr)) {
        return fromPreferred;
      }
      return execAddr;
    }
  } catch (err) {
    console.warn('[wallet] resolve exec Privy for WALLET session failed', err);
  }

  if (fromPreferred) return fromPreferred;

  const cached = getWalletAddress();
  if (cached) return cached;

  if (!getAccessToken()) return '';

  try {
    const profile = await getUserProfile();
    const fromProfile = pickWalletFromProfile(profile);
    if (fromProfile) {
      setWalletAddress(fromProfile);
      return fromProfile;
    }
  } catch (err) {
    console.warn('[wallet] resolveTradeUserAddress failed', err);
  }

  return '';
}
